import { client } from "../net/client";
import { useSignal } from "../net/signal";
import type { RoomState } from "../gen/bazivan/v1/table_pb";
import { fa, faNum } from "../game/cards";
import { avatarSrc } from "../game/avatar";
import { carpets, thumbOf, isCarpet } from "../game/carpet";
import { BotIcon } from "./BotIcon";
import { placeLabel } from "../game/place";

/** اتاقِ خصوصی: کد را می‌دهی، دوستت با همان کد می‌آید. */
export function Room({ room }: { room: RoomState }) {
  const myId = useSignal(client.playerId);
  const host = room.hostId === myId;
  const filled = room.seats.filter((s) => s.playerId).length;

  return (
    <div className="page stack">
      <div className="plate" style={{ textAlign: "center" }}>
        <div className="muted">کدِ اتاق</div>
        <div style={{ fontSize: 40, fontWeight: 700, letterSpacing: 10, color: "var(--gold-bright)", direction: "ltr" }}>
          {room.code}
        </div>
        <button className="btn ghost" style={{ marginTop: 10 }} onClick={() => share(room.code)}>
          هم‌رسانیِ کد
        </button>
      </div>

      <div className="plate stack">
        {room.seats.map((s) => (
          <div key={s.index} className="row" style={{ justifyContent: "space-between" }}>
            <span className="row" style={{ gap: 10 }}>
              {s.playerId
                ? <img className="seatface" src={avatarSrc(s.avatar)} alt="" width={34} height={34} />
                : <span className="seatface empty" />}
              <span className="row" style={{ gap: 6 }}>
                {s.playerId && s.bot && <BotIcon />}
                {s.playerId ? (s.name || "بازیکن") : "— خالی —"}
                {s.playerId && placeLabel(s) && <span className="plc-tag">{placeLabel(s)}</span>}
              </span>
            </span>
            <span className="muted">صندلیِ {fa(s.index + 1)}</span>
          </div>
        ))}
      </div>

      {/* فرشِ اتاق: میزبان از دکمه‌ی سرصفحه عوضش می‌کند؛ همه همین را می‌بینند */}
      <div className="plate row" style={{ justifyContent: "space-between" }}>
        <span className="row" style={{ gap: 10 }}>
          <img src={thumbOf(isCarpet(room.carpet) ? room.carpet : "eyvan")} alt="" width={34} height={34} style={{ borderRadius: 8 }} />
          <span>فرشِ میز: <b>{(carpets.find((c) => c.key === room.carpet) ?? carpets[0]!).fa}</b></span>
        </span>
        {host ? <span className="muted">از دکمه‌ی فرش در بالا</span> : <span className="muted">انتخابِ میزبان</span>}
      </div>

      {/* میزِ عمومی: میزبان روشن و خاموشش می‌کند، ولی **همه** می‌بینندش — کسی
          که سرِ میز می‌نشیند حق دارد بداند دارد پخش می‌شود. */}
      <div className="plate row" style={{ justifyContent: "space-between" }}>
        <span>{room.isPublic ? "میزِ عمومی — بقیه می‌توانند تماشا کنند" : "میزِ خصوصی"}</span>
        {host ? (
          <button className="chip" onClick={() => client.setPublic(!room.isPublic)}>
            {room.isPublic ? "خصوصی‌اش کن" : "عمومی‌اش کن"}
          </button>
        ) : (
          <span className="muted">انتخابِ میزبان</span>
        )}
      </div>

      {/* چتِ میز: مثلِ «میزِ عمومی» انتخابِ میزبان است و دیده‌ی همه. وسطِ بازی
          هم می‌شود بستش — همان‌جا که معمولاً لازم می‌شود. */}
      <div className="plate row" style={{ justifyContent: "space-between" }}>
        <span>{room.chatOpen ? "چتِ میز باز است" : "چتِ میز بسته است"}</span>
        {host ? (
          <button className="chip" onClick={() => client.setChatOpen(!room.chatOpen)}>
            {room.chatOpen ? "ببندش" : "بازش کن"}
          </button>
        ) : (
          <span className="muted">انتخابِ میزبان</span>
        )}
      </div>

      {/* ورودیِ میز: خواندنی، و برای مهمان مهم‌تر از میزبان — او باید پیش از
          نشستن بداند چقدر از کیفش کم می‌شود. */}
      {room.stake > 0n && (
        <div className="plate row" style={{ justifyContent: "space-between" }}>
          <span>ورودیِ میز: <b>{faNum(room.stake)} سکه</b></span>
          <span className="muted">۱۰٪ برشِ خانه</span>
        </div>
      )}

      {/* ربات سرِ میزِ ورودی‌دار می‌نشیند ولی پول نمی‌گذارد؛ میز تا وقتی
          دستِ‌کم دو آدم دارد ورودی‌دار می‌مانَد. میزبان باید پیش از زدنِ
          «شروع» بداند کدام حالت است — [[economy.StakeAllowed]]. */}
      {host && room.stake > 0n && filled < room.seats.length && (
        <div className="plate muted">
          {filled >= 2
            ? `${fa(room.seats.length - filled)} صندلی با ربات پر می‌شود. ربات ورودی نمی‌گذارد و از گلدان هم چیزی نمی‌برد — ورودی فقط از ${fa(filled)} آدمِ سرِ میز گرفته می‌شود.`
            : "میزِ ورودی‌دار دستِ‌کم دو آدم می‌خواهد. با این تعداد، میز رایگان می‌شود و ورودی گرفته نمی‌شود."}
        </div>
      )}

      {host ? (
        <button className="btn" onClick={() => client.startRoom()}>
          شروع {filled < room.seats.length ? `(${fa(room.seats.length - filled)} ربات)` : ""}
        </button>
      ) : (
        <div className="muted" style={{ textAlign: "center" }}>منتظرِ میزبان…</div>
      )}

      <button className="btn ghost" onClick={() => client.leaveRoom()}>بیرون رفتن</button>
    </div>
  );
}

function share(code: string): void {
  const url = `${location.origin}${location.pathname}?room=${code}`;
  const text = `بیا «بازی وان» بازی کنیم — کدِ اتاق ${code}\n${url}`;
  if (navigator.share) void navigator.share({ text }).catch(() => { /* لغوِ کاربر */ });
  else void navigator.clipboard?.writeText(text);
}
